import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { ChevronRight, Trash2, User, Shield, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AccountScreen = () => {
  const { setCurrentScreen, setActiveTab } = useApp();
  const { user, logout, getAuthHeaders } = useAuth();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const signInMethod = user?.email ? 'Google' : (user?.phone ? 'Phone (OTP)' : 'Not set');

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/delete-account`, {
        method: 'DELETE',
        headers: { ...getAuthHeaders() }
      });
      const data = await response.json();
      if (response.ok) {
        alert(data.message || 'Account deleted successfully');
        logout();
        setActiveTab('home');
        setCurrentScreen('home');
      } else {
        alert('Failed to delete account.');
      }
    } catch (error) {
      alert('Error deleting account.');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pb-24">
      <div className="px-6 pt-6 pb-4 flex items-center gap-4 border-b border-black/10">
        <button
          onClick={() => setCurrentScreen('profile')}
          className="w-10 h-10 bg-black/5 rounded-xl flex items-center justify-center hover:bg-black/10 transition-all flex-shrink-0"
        >
          <ChevronRight size={18} className="text-black rotate-180" />
        </button>
        <button onClick={() => { setActiveTab('home'); setCurrentScreen('home'); }} className="hover:opacity-80 transition-opacity"><img src="/logos/logo-black.jpg" alt="ATELIA" className="h-16 w-auto object-contain" /></button>
      </div>

      <div className="px-6 pt-6 pb-4">
        <p className="text-xs uppercase tracking-wide text-black/55 mb-2 font-bold">USER ACCOUNT</p>
        <h1 className="text-2xl font-bold text-black uppercase tracking-wide">Account Settings</h1>
      </div>

      {/* Account Info */}
      <div className="px-6 mb-6">
        <div className="bg-white border border-black/12 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-3">
            <User size={20} className="text-black/55" />
            <p className="text-sm font-bold text-black uppercase">Account Information</p>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-black/55">Name:</span>
              <span className="font-bold text-black">{user?.name || 'Not set'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-black/55">Email:</span>
              <span className="font-bold text-black">{user?.email || 'Not set'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-black/55">Phone:</span>
              <span className="font-bold text-black">{user?.phone || 'Not set'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-black/55">Sign-in method:</span>
              <span className="font-bold text-black">{signInMethod}</span>
            </div>
            {user?.role === 'admin' && (
              <div className="flex justify-between items-center">
                <span className="text-black/55">Access:</span>
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide"
                  style={{ background: '#800020', color: '#fff' }}>
                  <Shield size={10} /> Admin
                </span>
              </div>
            )}
          </div>
        </div>
        <button
          onClick={() => setCurrentScreen('profile')}
          className="mt-3 w-full bg-white border border-black/12 rounded-xl p-3.5 flex items-center justify-between hover:bg-black/5"
        >
          <div className="flex items-center gap-3">
            <User size={18} className="text-black" />
            <span className="text-sm font-bold text-black uppercase">Edit name &amp; phone</span>
          </div>
          <ChevronRight size={16} className="text-black/30" />
        </button>
      </div>

      {/* Danger Zone */}
      <div className="px-6">
        <p className="text-xs uppercase tracking-wide text-black/55 mb-3 font-bold">DANGER ZONE</p>
        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="w-full bg-white border border-red-200 rounded-xl p-4 flex items-center justify-between hover:bg-red-50"
        >
          <div className="flex items-center gap-3">
            <Trash2 size={18} className="text-red-600" />
            <div className="text-left">
              <p className="text-sm font-bold text-red-600 uppercase">Delete Account</p>
              <p className="text-xs text-black/55 mt-0.5">Permanently remove account</p>
            </div>
          </div>
          <ChevronRight size={16} className="text-red-300" />
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center px-6" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={32} className="text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-black text-center mb-2 uppercase">Delete Account?</h2>
            <p className="text-sm text-black/72 text-center mb-6">
              This cannot be undone. All scans, preferences, and data will be permanently deleted.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-3 bg-white border border-black/12 rounded-xl text-sm font-bold uppercase hover:bg-black/5"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="flex-1 py-3 bg-red-600 text-white rounded-xl text-sm font-bold uppercase hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountScreen;